import { DAY_FLAGS } from "../utils/dayFlags";

// One pill per day-type flag, shown in the FLAGS column of both Time Tracking
// surfaces (PRD_TimeRecordDayFlags C6; formerly LateBadge, BL-005).
//
// The palette is ADOPTED from the payroll Gross Income Breakdown's type-badge
// styles (src/styles/sections/payroll.css:4544-4563) rather than invented, so a
// colour cannot mean two different things on two screens of one app. C6
// documented three deliberate deviations; two remain:
//   1. Piece Rate is solid deep emerald, not payroll's amber — amber IS Late in
//      this column, and two identical pills would be unreadable. It fills with
//      the darkest emerald the palette offers (--ui-success-text).
//   2. No uppercase — this pill reads "Late" and "Paid Leave"; payroll's day
//      badges render in capitals. (C6's other one, spelling out "Paid Leave"
//      rather than "Leave-P", ended when payroll's badges were spelled out too.)
//
// Solid fill = consequential (Piece Rate, Absent); pale fill = informational
// (Late, Paid Leave, Unpaid Leave). That is how payroll already separates its
// solid Absent from its pale Unpaid leave, and it is what keeps the two greens and
// the two reds apart in one column.
//
// Contrast is no longer claimed here in prose — that is exactly how the old
// numbers went stale when the palette moved. DayFlagBadge.test.js resolves each
// role out of globals.css and measures it. All five now clear AA at 12px. Paid
// Leave sat at 4.36:1 for one release and was pinned there as a known
// palette-level gap; it was never specific to this component (the same
// --ui-success-text-on---ui-success-tint pair was failing app-wide), so the fix
// landed in the palette — --ui-success-text now resolves to --brand-emerald-deep
// (#117A5A) and the pair measures 4.72:1. Absent went
// the other way: white on the old #EF4444 was 3.76:1 and had been failing since
// this component shipped; on --ui-danger it is 6.57:1.
//
// One shared definition so the styling can't drift — it previously did:
// EmployeeTimeRecords used 2px 8px / 0.75rem while TimeTracking used 1px 6px /
// 0.7rem. The optional `style` merges in for placement only. (BL-005)
//
// NOTE: all three current call sites pass `flag` alone — two FLAGS table cells (the
// monthly table and the roster) and the monthly mobile record card, which is a plain
// <div>, not a cell. So `style` has no production caller today. It is kept per C6 (and
// exercised by the suite) because it is the seam that stops a future placement —
// a card, a summary line — from hand-rolling its own pill. Its original consumer
// was the roster's inline-beside-Time-In render, deleted when C10 gave the roster
// its own column.
// Exported for DayFlagBadge.test.js. The colour half of this map cannot be
// asserted through the rendered DOM: jsdom's cssstyle DROPS any var() value, so
// `toHaveStyle({ background: "var(--ui-danger)" })` compares "" to "" and passes
// against every role — including one that does not exist. The suite pins the map
// directly instead, and feeds these role names into its contrast block so the
// mapping and the measured ratio are checked against the same source.
export const FLAG_STYLES = {
  late: { background: "var(--ui-warning-tint)", color: "var(--ui-warning-text)" },
  "piece rate": { background: "var(--ui-success-text)", color: "var(--ui-surface)" },
  "paid leave": { background: "var(--ui-success-tint)", color: "var(--ui-success-text)" },
  "unpaid leave": { background: "var(--ui-danger-tint)", color: "var(--ui-danger-text)" },
  absent: { background: "var(--ui-danger)", color: "var(--ui-surface)" },
};

export function DayFlagBadge({ flag, style }) {
  const palette = FLAG_STYLES[flag];
  const label = DAY_FLAGS[flag]?.label;
  if (!palette || !label) return null;

  return (
    <span
      style={{
        // display + whiteSpace are load-bearing: without inline-block a span's
        // vertical padding does not expand the line box, and without nowrap the
        // two-word labels wrap inside the pill.
        display: "inline-block",
        whiteSpace: "nowrap",
        padding: "2px 8px",
        borderRadius: "9999px",
        fontSize: "0.75rem",
        fontWeight: 600,
        ...palette,
        ...style,
      }}
    >
      {label}
    </span>
  );
}
